import { Col, Progress, Row, Spin } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';

import FarmerSearch from './components/FarmerSearch';
import Filters from './components/filters';
import Header from './components/Header';
import Map from './components/map';

import usePipeDataQuery from './hooks/usePipeDataQuery';

import { formatMapData } from './helper';

import './style.scss';

import useCountryAndSeasonIndexFilter from 'hooks/useCountryAndSeasonIndexFilter';

const Pipes = () => {
  const {
    currentCountry: country,
    currentSeasonIdx: seasonIndex,
    setCurrentCountry: setCountry,
    setCurrentSeasonIdx: setSeasonIndex,
  } = useCountryAndSeasonIndexFilter();

  const [searchTerm, setSearchTerm] = useState(null);

  const [appliedFilters, setAppliedFilters] = useState([]);
  const [selectedPipe, setSelectedPipe] = useState(null);

  const filterParams = useMemo(() => {
    const extractedFilters = appliedFilters?.reduce((acc, filter) => {
      const filterMap = {
        agronomist: 'agronomistId.in',
        district: 'district.in',
        status: 'status.in',
      };

      const { type, value } = filter;
      acc[filterMap[type]] = acc[filterMap[type]]
        ? `${acc[filterMap[type]]},${value}`
        : value;
      return acc;
    }, {});

    const params = {
      projection: 'MrvResourcesListWithCubeAndTask',
      sort: 'id,asc',
      ...(country && { 'country.equals': country }),
      ...(seasonIndex?.length && { 'seasonTypeId.equals': seasonIndex }),
      ...(searchTerm && { 'farmName.contains': searchTerm }),
      ...extractedFilters,
    };

    return params;
  }, [appliedFilters, country, seasonIndex, searchTerm]);

  const {
    isLoading,
    error,
    data,
    isFetching,
    progress,
    currentPage,
    totalPages,
  } = usePipeDataQuery(filterParams);

  const markers = useMemo(() => {
    if (data) {
      return formatMapData(data);
    }
    return [];
  }, [data]);

  const handleFilterChange = useCallback(
    (selectedFilter) => {
      const isExisting = appliedFilters.find(
        (filter) => filter?.value === selectedFilter?.value,
      );

      const updatedFilters = isExisting
        ? appliedFilters?.filter(
            (filter) => filter?.value !== selectedFilter?.value,
          )
        : [...appliedFilters, selectedFilter];

      setAppliedFilters(updatedFilters);
    },
    [appliedFilters],
  );

  const handleFilterClear = () => setAppliedFilters([]);

  const handleMarkerClick = (marker) => setSelectedPipe(marker);

  useEffect(() => {
    setAppliedFilters((prev) => prev.filter((f) => f?.type === 'status'));
  }, [country]);

  return (
    <div className="pipes">
      <Header
        country={country}
        seasonIndex={seasonIndex}
        onCountryChange={setCountry}
        onSeasonIndexChange={setSeasonIndex}
      />
      <Row>
        <Col span={24} className="table-container">
          <Row className="filters">
            <FarmerSearch onSearch={setSearchTerm} />
            <Filters
              country={country}
              appliedFilters={appliedFilters}
              onFilterChange={handleFilterChange}
              onFilterClear={handleFilterClear}
            />
            {Math.round(progress) < 100 && progress > 0 && (
              <Progress
                percent={Math.round(progress)}
                type="circle"
                size={50}
                strokeColor={{
                  '0%': '#108ee9',
                  '100%': '#87d068',
                }}
                strokeWidth={10}
              />
            )}
          </Row>
          <Spin
            spinning={isLoading && currentPage === 1}
            size="large"
            style={{ maxHeight: 'unset' }}
          >
            <Map
              country={country}
              selectedMarker={selectedPipe}
              onMarkerClick={handleMarkerClick}
              markers={markers}
              error={error}
              isFetching={isFetching}
              currentPage={currentPage}
              totalPages={totalPages}
            />
          </Spin>
        </Col>
      </Row>
    </div>
  );
};

export default Pipes;
